import { OverlayModule } from '@angular/cdk/overlay';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { UserServiceCardFullModule } from '@app/meetings/user-service-card-full/user-service-card-full.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { ServiceCardDisplayModeType } from '@core/components/service-card/types';
import { UserServiceCardModule } from '@core/components/service-card/user-service-card.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { PipesModule } from '@core/pipes/pipes.module';

import { HelpersBffService } from '@core/services/bff/helpers.service';
import { IconComponent } from '@shared/icon/icon.component';
import { addDays, isSameDay, isToday, isTomorrow } from 'date-fns';
import { UserService } from 'lingo2-models';
import { FormatPipeModule } from 'lingo2-ngx-date-fns';
import { InViewportModule } from 'ng-in-viewport';

import { UserServiceSurfaceBlockComponent } from '../../../meetings/user-service-card-full/user-service-surface-block/user-service-surface-block.component';

@Component({
  selector: 'app-services-drag-scroll',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,
    PipesModule,

    FormatPipeModule,
    OverlayModule,
    UserServiceCardModule,
    InViewportModule,
    ModalModule,
    UserServiceCardFullModule,
    MobileModalModule,
    IconComponent,
    UserServiceSurfaceBlockComponent,
  ],
  templateUrl: './services-drag-scroll.component.html',
  styleUrls: ['./services-drag-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicesDragScrollComponent extends AbstractPane {
  @ViewChild('scroller', { read: CdkVirtualScrollViewport })
  scroller: CdkVirtualScrollViewport;

  public isDragScrollEnabled: boolean = this.deviceService.isDesktop();
  public cardSizeTable: any = { width: 277, height: 549, scrollItem: 1, min_units: 3 };
  public filters: Date[];
  public indexFilterSelected = 0;
  public userServiceCardFullInModal: boolean;
  public openedUserService: UserService;
  public surfaceBlockDisplay: boolean;

  private originalUrl: string;

  protected location = inject(Location);
  private helpers = inject(HelpersBffService);

  protected onContentLoaded(viaQuery?: boolean): void {
    this.cardSizeTable = this.setCardSizeTable();
    this.isVisible = !!this._pane?.contentItems?.length || !!this._pane?.userServices?.length;
    this.initFilter();
  }

  public setCardSizeTable(): { width: number; height: number; scrollItem: number; min_units: number } {
    if (this.deviceService.isDesktop() || this.deviceService.isTablet()) {
      switch (this.userServicesView()) {
        case 'narrow':
          return { width: 277, height: 549, scrollItem: 1, min_units: 3 };
        case 'wide':
          return { width: 830, height: 140, scrollItem: 1, min_units: 1 };
        default:
          return { width: 277, height: 549, scrollItem: 1, min_units: 3 };
      }
    }

    if (this.deviceService.isMobile()) {
      switch (this.userServicesView()) {
        case 'narrow':
          return { width: 277, height: 549, scrollItem: 1, min_units: 3 };
        case 'wide':
          return { width: 830, height: 140, scrollItem: 1, min_units: 1 };
        default:
          return { width: 277, height: 549, scrollItem: 1, min_units: 3 };
      }
    }
  }

  public chunk(arr, size = 1) {
    return arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);
  }

  public rows(): number {
    if (this.deviceService.isMobile() && this.pane?.user_services_view === 'wide') {
      return 1;
    }

    if (this._pane?.contentItems?.length < this.pane?.rows) {
      return this._pane?.contentItems?.length;
    } else if (this.userServices.length < this.pane?.rows) {
      return this.userServices.length;
    }
    return this.pane?.rows || 1;
  }

  public userServicesView(): ServiceCardDisplayModeType {
    return this.pane?.user_services_view as ServiceCardDisplayModeType;
  }

  public moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  public moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    this.scroller.scrollToOffset(offset + this.cardSizeTable.width * this.cardSizeTable.scrollItem, 'smooth');
  }

  public display(date: Date): any {
    // string | Date
    if (isToday(date)) {
      return 'library.meeting-drag-scroll.today';
    } else if (isTomorrow(date)) {
      return 'library.meeting-drag-scroll.tomorrow';
    }
    return date;
  }

  public selectFilter(index: number) {
    this.indexFilterSelected = index;
  }

  public userServicesFilter(): UserService[] {
    if (this._pane?.contentItems) {
      return this._pane?.contentItems?.filter((_service) =>
        isSameDay(new Date(_service.created_at), this.filters[this.indexFilterSelected]),
      );
    } else {
      return this.userServices.filter((_service) =>
        isSameDay(new Date(_service.created_at), this.filters[this.indexFilterSelected]),
      );
    }
  }

  public onToggleUserServiceInModal(userService: UserService = null) {
    this.openedUserService = userService;
    this.userServiceCardFullInModal = !this.userServiceCardFullInModal;
    this.setAndResetUrl();
    this.markForCheck();
  }

  public onIinViewportChange(e) {
    this.surfaceBlockDisplay = e;
    this.markForCheck();
  }

  public trackByFn(index, item: UserService): string {
    return item.id;
  }

  public onInViewportBottomChange(reached) {
    if (reached.visible) {
      this.loadNextPage();
    }
  }

  private initFilter() {
    this.filters = Array.from({ length: 7 }, (item, index) => addDays(new Date(), index));
  }

  private setAndResetUrl() {
    const url = this.helpers.userServiceRouteUniversal(this.openedUserService);
    const currentUrl = this.location.path(false);
    if (this.userServiceCardFullInModal) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }
}
